import './ClaimDetails.scss';

import * as React from 'react';
import moment from 'moment-timezone';

import { Adjustment, Remark, BillingProvider, Claim, Demographic, Payer, Provider, State } from '../../redux/claim/types';
import { ToastParams } from '../../redux/message/types';
import {
  Breadcrumb,
  CommandBarButton,
  DatePicker,
  Dropdown,
  IDropdownOption,
  Spinner,
  SpinnerSize,
  TextField,
  Toggle,
  CommandBar,
  ICommandBarItemProps,
  MessageBar,
  MessageBarType,
} from '@fluentui/react';

import { Clipboard } from '../core/Clipboard';
import { AsyncData } from '../../redux/core';
import { ClaimCodeLookup } from './ClaimCodeLookup';
import { HealAPI } from '../../healApi';
import { ROUTES } from '../../config/routes';
import { OkCancelDialog } from '../../components/core/OkCancelDialog';
import { DocumentTitle } from '../../components/core/DocumentTitle';
import { cancelClaim, refreshERA } from '../../api';
import { NavigateFunction } from 'react-router-dom';

declare const healConfig: any;

export interface OwnProps {
  params?: any;
  navigate: NavigateFunction;
}

export interface StateProps {
  claim: AsyncData<Claim>;
  optionKeyDepressed: boolean;
}

export interface DispatchProps {
  actions: {
    toast: (params: ToastParams) => void;
    onPageDidMount: (name: string) => void;
    onPageWillUnmount: (name: string) => void;
    submitClaim: (claimId: string) => void;
    refreshClaim: (claimId: string) => void;
    resetClaim: (claimId: string) => void;
    generateClaim: (visitCode: string, ignoreOldDataCheck: boolean, forceFinalizedClaimReplacement: boolean) => void;
    fetchOneClaim: (claimId: string) => void;
  };
}

type ClaimDetailsProps = OwnProps & StateProps & DispatchProps;

export interface ClaimDetailsState {
  claim?: any;
  payerList: Payer[];
  billingProviderList: BillingProvider[];
  unsaved: boolean;
  placeOfService?: string | null;
  isSaving: boolean;
  isDependent?: boolean;
  shouldComponentUpdate?: boolean;
  canEdit?: boolean;
  latestClaimIdIfDifferent?: string;
}

export class ClaimDetails extends React.Component<ClaimDetailsProps, ClaimDetailsState> {
  private static PATIENT_SEX_OPTIONS: { key: string; text: string }[] = [
    { key: 'F', text: 'Female' },
    { key: 'M', text: 'Male' },
    { key: 'O', text: 'Other' },
  ];

  private static PATIENT_RELATIONSHIP_OPTIONS: { key: string; text: string }[] = [
    { key: 'Child', text: 'Child' },
    { key: 'Spouse', text: 'Spouse' },
  ];

  private static PLACE_OF_SERVICE_OPTIONS: { key: string; text: string }[] = [
    { key: 'Home', text: 'Home' },
    { key: 'Office', text: 'Office' },
    { key: 'Telehealth', text: 'Telehealth' },
  ];

  constructor(props: ClaimDetailsProps) {
    super(props);
    this.state = {
      payerList: [],
      billingProviderList: [],
      unsaved: false,
      isSaving: false,
    };
  }

  public componentDidMount() {
    this.props.actions.onPageDidMount('Claims');
    this.props.actions.fetchOneClaim(this.props.params.id);

    HealAPI.fetchPayers().then((response) => {
      if (response.data) {
        this.setState({ payerList: response.data });
      }
    });

    HealAPI.fetchBillingProviders().then((response) => {
      if (response.data) {
        this.setState({ billingProviderList: response.data });
      }
    });
  }

  public componentDidUpdate(prevProps: ClaimDetailsProps) {
    if (this.props.claim.data && prevProps.claim.data !== this.props.claim.data) {
      const { data } = this.props.claim;
      const serviceLines = data.serviceLines.map((sl) => ({
        ...sl,
        icdPointers: sl.icdPointers.join(','),
        modifiers: sl.modifiers.join(','),
      }));

      const claim = {
        ...data,
        serviceLines,
        dateOfService: moment(data.dateOfService),
        subscriber: {
          ...data.subscriber,
          dob: moment(data.subscriber.dob),
        },
        dependent: data.dependent
          ? {
              ...data.dependent,
              dob: moment(data.dependent.dob),
            }
          : null,
      };
      this.setState({
        claim,
        isDependent: !!claim.dependent,
        unsaved: false,
        canEdit: data.state === State.Draft || data.state === State.ValidationFailed,
        placeOfService: data.serviceLines.length > 0 ? data.serviceLines[0].placeOfService : undefined,
      });

      const shouldCheckForLatestClaim = !!claim && claim.state === State.Replaced;
      if (shouldCheckForLatestClaim) {
        HealAPI.fetchClaimsByVisitCode(claim.externalReferenceId, true).then((response) => {
          const latestClaimId = response?.data?.[0].id;
          if (response.ok && latestClaimId && latestClaimId !== claim.id) {
            this.setState({ latestClaimIdIfDifferent: latestClaimId });
          }
        });
      }
    }
  }

  public shouldComponentUpdate(nextProps: ClaimDetailsProps, nextState: ClaimDetailsState) {
    return (
      nextState.shouldComponentUpdate ||
      nextProps.claim !== this.props.claim ||
      nextState.claim !== this.state.claim ||
      nextState.unsaved !== this.state.unsaved ||
      nextState.isDependent !== this.state.isDependent ||
      nextState.isSaving !== this.state.isSaving ||
      nextState.payerList !== this.state.payerList ||
      nextState.billingProviderList !== this.state.billingProviderList ||
      nextState.canEdit !== this.state.canEdit ||
      nextProps.optionKeyDepressed !== this.props.optionKeyDepressed ||
      nextState.latestClaimIdIfDifferent !== this.state.latestClaimIdIfDifferent
    );
  }

  public componentWillUnmount() {
    this.props.actions.onPageWillUnmount('Claims');
  }

  public render(): JSX.Element {
    const claim = this.state.claim;

    if (!claim) {
      return (
        <div style={{ marginTop: 100 }}>
          <Spinner size={SpinnerSize.large} />
        </div>
      );
    }

    return (
      <div>
        <DocumentTitle title={`${this.props.claim?.data?.externalReferenceId || '...'} Claim`} />

        <Breadcrumb
          items={[
            { text: 'Claims', key: 'Claims', onClick: this.onClaimsClick },
            { text: `${claim.externalReferenceId} (rev. ${claim.revision}) - ${claim.metadata.service_code}`, key: 'ClaimSpecific' },
          ]}
          // Returning undefined to OnReduceData tells the breadcrumb not to shrink
          onReduceData={() => undefined}
        />

        <div className="flex" style={{ position: 'relative', marginLeft: 10 }}>
          <div className="flex flex-column" style={{ flex: 2 }}>
            {this.renderActionButtons()}
            {this.renderSubmissionDetails()}
            {this.renderPatientDetails()}
          </div>

          <div className="flex flex-column ms-depth-4" style={{ flex: 1, padding: 14, background: '#eee' }}>
            <div style={{ fontSize: '2em', fontWeight: 'bold' }}>{claim.state}</div>
            {this.renderDetailedStatus()}
            {this.renderErrors()}
            {this.renderFinancialsAndDates()}
            <div style={{ position: 'absolute', top: 10, right: 10 }}>
              <Clipboard className="cursor-copy" content={claim.processorId} message="Eligible reference id copied to clipboard">
                {claim.processorId}
              </Clipboard>
            </div>
          </div>
        </div>

        <div className="flex flex-column" style={{ marginLeft: 10 }}>
          <div style={{ flex: 1 }}>
            <div className="section-header">Payer</div>
            {this.renderPayer()}
          </div>
          <div style={{ flex: 1 }}>
            <div className="section-header">Billing Provider</div>
            {this.renderBillingProvider()}
          </div>
        </div>

        <div className="flex flex-column" style={{ marginLeft: 10, marginBottom: 10 }}>
          <div style={{ flex: 1 }}>
            <div className="section-header">Procedures</div>
            {this.renderServiceLines()}
            <div className="flex" style={{ marginTop: 20, height: 32 }}>
              {this.state.canEdit && <CommandBarButton iconProps={{ iconName: 'Add' }} text="Add Line" onClick={this.addServiceLine} />}
            </div>
          </div>
        </div>

        <div className="flex flex-column" style={{ marginLeft: 10, marginBottom: 20 }}>
          <div style={{ flex: 1 }}>
            <div className="section-header">Diagnosis Codes (ICD-10)</div>
            <div style={{ marginTop: 20 }}>{this.renderDiagnosisCodes()}</div>
            <div className="flex" style={{ marginTop: 20, height: 32 }}>
              {this.state.canEdit && <CommandBarButton iconProps={{ iconName: 'Add' }} text="Add Code" onClick={this.addDiagnosisCode} />}
            </div>
          </div>
        </div>
      </div>
    );
  }

  private onClaimsClick = () => {
    this.props.navigate(ROUTES.claims, { replace: true });
  };

  private renderActionButtons(): JSX.Element {
    let showSubmit = false;
    let showSave = true;
    let showDiscard = true;
    let showCheckForUpdates = false;
    const { claim, latestClaimIdIfDifferent } = this.state;

    if (claim) {
      showSubmit = claim.state === State.Draft || claim.state === State.ValidationFailed;
      showCheckForUpdates = claim.state !== State.Draft && claim.state !== State.ValidationFailed;
      showSave = claim.state === State.Draft || claim.state === State.ValidationFailed;
      showDiscard = showSave;
    }

    const items: ICommandBarItemProps[] = [];

    if (showSave) {
      items.push({
        key: 'save',
        text: 'Save',
        disabled: !this.state.unsaved,
        iconProps: { iconName: 'Save' },
        onClick: this.saveClaimDetails,
      });
    }

    if (showDiscard) {
      items.push({
        key: 'discard',
        text: 'Discard',
        disabled: !this.state.unsaved,
        iconProps: { iconName: 'RevToggleKey' },
        onClick: () => claim && this.props.actions.fetchOneClaim(claim.id),
      });
    }

    if (showSubmit) {
      items.push({
        key: 'submit',
        text: 'Submit',
        disabled: this.state.unsaved,
        iconProps: { iconName: 'Send' },
        onClick: () => claim && this.props.actions.submitClaim(claim.id),
      });
    }

    items.push({
      key: 'generate',
      text: 'Re-Generate',
      iconProps: { iconName: 'Refresh' },
      onClick: () => {
        if (claim) {
          if (claim.state === State.Finalized) {
            OkCancelDialog.show({
              title: 'Re-Generate claim',
              subText: 'This claim is already Finalized.  Are you sure you want to re-generate and resubmit it?',
              onOk: () => this.props.actions.generateClaim(claim.externalReferenceId, true, true),
            });
          } else {
            this.props.actions.generateClaim(claim.externalReferenceId, true, false);
          }
        }
      },
    });

    if (showCheckForUpdates) {
      items.push({
        key: 'checkUpdate',
        text: 'Check for Updates',
        iconProps: { iconName: 'CloudDownload' },
        onClick: () => claim && this.props.actions.refreshClaim(claim.id),
      });
    }

    if (latestClaimIdIfDifferent) {
      items.push({
        key: 'latestClaim',
        text: 'See latest claim',
        iconProps: { iconName: 'Forward' },
        href: `${ROUTES.claims}/${latestClaimIdIfDifferent}`,
      });
    }

    if (this.props.optionKeyDepressed) {
      items.push({
        key: 'reset',
        text: 'Reset',
        iconProps: { iconName: 'removeOccurrence' },
        onClick: () => claim && this.props.actions.resetClaim(claim.id),
      });
    }

    const farItems = [
      {
        key: 'request',
        text: 'Claim Submission Request',
        iconProps: { iconName: 'Forward' },
        target: '_blank',
        href: claim ? `https://insuranceservice.${healConfig.env}-uswest2.heal.com/v1/txlog/eligible_claim_submission_request?id=${claim.id}_${claim.revision}` : undefined,
      },
      {
        key: 'response',
        text: 'Claim Submission Response',
        iconProps: { iconName: 'Back' },
        target: '_blank',
        href: claim ? `https://insuranceservice.${healConfig.env}-uswest2.heal.com/v1/txlog/eligible_claim_submission_response?id=${claim.id}_${claim.revision}` : undefined,
      },
      {
        key: 'acknowledgements',
        text: 'Eligible Acknowledgements',
        iconProps: { iconName: 'ReceiptCheck' },
        target: '_blank',
        href: claim ? `https://insuranceservice.${healConfig.env}-uswest2.heal.com/v1/eligible/acknowledgements?processorId=${claim.processorId}` : undefined,
      },
      {
        key: 'paymentStatus',
        text: 'Eligible Payment Status',
        iconProps: { iconName: 'DocumentManagement' },
        target: '_blank',
        href: claim ? `https://insuranceservice.${healConfig.env}-uswest2.heal.com/v1/eligible/payment_status?processorId=${claim.processorId}` : undefined,
      },
      {
        key: 'paymentReport',
        text: 'Eligible Payment Reports',
        iconProps: { iconName: 'Money' },
        target: '_blank',
        href: claim ? `https://insuranceservice.${healConfig.env}-uswest2.heal.com/v1/eligible/payment_reports?processorId=${claim.processorId}` : undefined,
      },
      {
        key: 'refreshERA',
        text: 'Refresh claim financials',
        iconProps: { iconName: 'Refresh' },
        target: '_blank',
        onClick: () => {
          refreshERA(claim.id);
        },
      },
    ];

    if (!claim.metadata.extends) {
      farItems.push({
        key: 'cancelClaim',
        text: 'Cancel claim',
        iconProps: { iconName: 'ChromeClose' },
        target: '_blank',
        onClick: () => {
          OkCancelDialog.show({
            title: 'Send Cancellation to the payer',
            subText:
              'Marks the current claim as being replaced, then submits a new revision as a cancellation. The cancelled version will be nearly identical (other than Frequency) to what was previously sent (e.g. if the existing revision was sent with the wrong tax id, the new revision must and will mimic that). Are you sure you want proceed?',
            onOk: () => cancelClaim(claim.externalReferenceId),
          });
        },
      });
    }

    return (
      <>
        <CommandBar items={items} overflowItems={showCheckForUpdates ? farItems : undefined} />
        {this.state.isSaving && <Spinner />}
      </>
    );
  }

  private renderSubmissionDetails(): JSX.Element | null {
    const { claim } = this.state;
    const freqList = [
      { key: 'First', text: 'First' },
      { key: 'Replacement', text: 'Replacement' },
      { key: 'Cancellation', text: 'Cancellation' },
    ];

    if (!claim) {
      return null;
    }

    return (
      <div>
        <div className="section-header">Submission Details</div>

        <div className="flex">
          <div style={{ flex: 0, marginRight: 10 }}>
            <TextField disabled={true} style={{ width: 100 }} label="Reference ID" value={claim.externalReferenceId} />
          </div>
          <div style={{ flex: 0, width: 130, marginRight: 10 }}>
            <Dropdown
              disabled={!this.state.canEdit}
              style={{ width: 130 }}
              label="Frequency"
              selectedKey={claim.frequency || ''}
              options={freqList}
              onChange={(a, v) => this.onChangedField(v, 'frequency', undefined, true)}
            />
          </div>
          <div style={{ flex: 1 }}>
            {claim.dateOfService && (
              <DatePicker
                disabled={!this.state.canEdit}
                allowTextInput={true}
                formatDate={this.onFormatDate}
                label="Date of Service"
                value={new Date(claim.dateOfService)}
                onSelectDate={(v) => this.onChangedField(v, 'dateOfService')}
              />
            )}
          </div>
          <div style={{ flex: 1, marginLeft: 10 }}>
            <Dropdown
              disabled={!this.state.canEdit}
              style={{ width: 130 }}
              label="Place of Service"
              selectedKey={this.state.placeOfService || undefined}
              options={ClaimDetails.PLACE_OF_SERVICE_OPTIONS}
              onChange={(a, v) =>
                v &&
                this.setState({
                  placeOfService: v.text,
                  unsaved: true,
                  shouldComponentUpdate: true,
                })
              }
            />
          </div>
          <div style={{ flex: 1 }}>
            <Toggle
              disabled={!this.state.canEdit}
              defaultChecked={this.state.isDependent}
              label="Patient is Dependent"
              onText="Yes"
              offText="No"
              onChange={() => this.setState({ unsaved: true, isDependent: !this.state.isDependent })}
            />
          </div>
        </div>
        {this.renderProvider('renderingProvider', claim.renderingProvider)}
        {this.renderProvider('supervisingProvider', claim.supervisingProvider)}
        <div className="flex">
          <div style={{ flex: 1, marginRight: 10 }}>
            <TextField
              disabled={!this.state.canEdit}
              multiline={true}
              rows={4}
              label="Additional Notes"
              value={claim.additionalNotes}
              onChange={(a, v) => this.onChangedField(v, 'additionalNotes')}
            />
          </div>
        </div>
      </div>
    );
  }

  private renderProvider(providerType: string, provider?: Provider): JSX.Element {
    const labelProviderType = providerType === 'renderingProvider' ? 'Rendering' : 'Supervising';
    return (
      <div className="flex">
        <div style={{ flex: 1, marginRight: 10 }}>
          <TextField
            disabled={!this.state.canEdit}
            label={labelProviderType + ' Provider NPI'}
            value={provider ? provider.npi : ''}
            onChange={(a, v) => this.onChangedField(v, providerType, 'npi')}
          />
        </div>
        <div style={{ flex: 1, marginRight: 10 }}>
          <TextField
            disabled={!this.state.canEdit}
            label={labelProviderType + ' Provider First Name'}
            value={provider ? provider.firstName : ''}
            onChange={(a, v) => this.onChangedField(v, providerType, 'firstName')}
          />
        </div>
        <div style={{ flex: 1, marginRight: 10 }}>
          <TextField
            disabled={!this.state.canEdit}
            label={labelProviderType + ' Provider Last Name'}
            value={provider ? provider.lastName : ''}
            onChange={(a, v) => this.onChangedField(v, providerType, 'lastName')}
          />
        </div>
      </div>
    );
  }

  private renderPatientDetails(): JSX.Element | null {
    const { claim } = this.state;

    if (!claim) {
      return null;
    }

    return (
      <>
        <div className="section-header">Dependent</div>
        {this.state.isDependent ? this.renderDemographic(claim.dependent || ({} as any), 'dependent', false, true, true) : 'No Dependent Information'}
        <div className="section-header">Subscriber</div>
        {claim.subscriber ? this.renderDemographic(claim.subscriber, 'subscriber', true, true, false) : 'No Subscriber Information'}
      </>
    );
  }

  private onChangedField = (value: any, field: string, subfield?: string, isFieldDropdown?: boolean, index?: number) => {
    let claim = { ...this.state.claim };
    value = isFieldDropdown ? value.key : value;

    if (claim && !claim[field]) {
      claim = { ...claim, [field]: {} };
    }

    if (subfield) {
      if (index !== undefined) {
        claim![field][index][subfield] = value;
      } else {
        claim![field][subfield] = value;
      }
    } else {
      if (index !== undefined) {
        claim![field][index] = value;
      } else {
        claim![field] = value;
      }
    }

    this.setState({ claim, unsaved: true, shouldComponentUpdate: isFieldDropdown });
  };

  private onChangedDemographicField = (type: string, value: any, field: string, subfield?: string, isFieldDropdown?: boolean) => {
    let claim = { ...this.state.claim };
    value = isFieldDropdown ? value.key : value;

    if (!claim[type]) {
      claim = {
        ...claim,
        [type]: {} as any,
      };
    }

    if (subfield) {
      if (!claim[type][field]) {
        claim[type] = {
          ...claim[type],
          [field]: {},
        };
      }
      claim[type][field][subfield] = value;
    } else {
      claim[type][field] = value;
    }
    this.setState({ claim, unsaved: true, shouldComponentUpdate: !!isFieldDropdown });
  };

  private renderDemographic(demographic: Demographic, type: string, showInsurance: boolean, showAddress: boolean, showRelationship: boolean): JSX.Element | null {
    let insuranceBlock: JSX.Element | null = null;
    let addressBlock: JSX.Element | null = null;

    if (showAddress) {
      const address = demographic.address;

      addressBlock = (
        <div>
          <div className="flex">
            <div style={{ flex: 1, marginRight: 10 }}>
              <TextField disabled={!this.state.canEdit} label="Phone" value={demographic.phone || ''} onChange={(a, v) => this.onChangedDemographicField(type, v, 'phone')} />
            </div>
            <div style={{ flex: 1, marginRight: 10 }}>
              <TextField
                disabled={!this.state.canEdit}
                required={true}
                label="Street Line 1"
                value={address ? address.streetLine_1 : ''}
                onChange={(a, v) => this.onChangedDemographicField(type, v, 'address', 'streetLine_1')}
              />
            </div>
            <div style={{ flexGrow: 0, marginRight: 10 }}>
              <TextField
                disabled={!this.state.canEdit}
                label="Street Line 2"
                value={address ? address.streetLine_2 : ''}
                onChange={(a, v) => this.onChangedDemographicField(type, v, 'address', 'streetLine_2')}
              />
            </div>
          </div>
          <div className="flex">
            <div style={{ flex: 1, marginRight: 10 }}>
              <TextField
                disabled={!this.state.canEdit}
                required={true}
                label="City"
                value={address ? address.city : ''}
                onChange={(a, v) => this.onChangedDemographicField(type, v, 'address', 'city')}
              />
            </div>
            <div style={{ flex: 1, marginRight: 10 }}>
              <TextField
                disabled={!this.state.canEdit}
                required={true}
                label="State"
                value={address ? address.state : ''}
                onChange={(a, v) => this.onChangedDemographicField(type, v, 'address', 'state')}
              />
            </div>
            <div style={{ flex: 1, marginRight: 10 }}>
              <TextField
                disabled={!this.state.canEdit}
                required={true}
                label="Zip"
                value={address ? address.zip : ''}
                onChange={(a, v) => this.onChangedDemographicField(type, v, 'address', 'zip')}
              />
            </div>
          </div>
        </div>
      );
    }

    if (showInsurance) {
      insuranceBlock = (
        <div>
          <div className="flex">
            <div style={{ flex: 1, marginRight: 10 }}>
              <TextField
                disabled={!this.state.canEdit}
                label="Member ID"
                value={demographic.memberId || ''}
                onChange={(a, v) => this.onChangedDemographicField(type, v, 'memberId')}
              />
            </div>
            <div style={{ flex: 1, marginRight: 10 }}>
              <TextField
                disabled={!this.state.canEdit}
                label="Group ID"
                value={demographic.groupId || ''}
                onChange={(a, v) => this.onChangedDemographicField(type, v, 'groupId')}
              />
            </div>
            <div style={{ flex: 1, marginRight: 10 }}>
              <TextField
                disabled={!this.state.canEdit}
                label="Group Name"
                value={demographic.groupName || ''}
                onChange={(a, v) => this.onChangedDemographicField(type, v, 'groupName')}
              />
            </div>
          </div>
        </div>
      );
    }

    let relationshipBlock: JSX.Element | null = null;
    if (showRelationship) {
      relationshipBlock = (
        <div style={{ flex: 1, marginRight: 10 }}>
          <Dropdown
            disabled={!this.state.canEdit}
            required={true}
            label="Relationship"
            onChange={(a, v) => this.onChangedDemographicField(type, v, 'relationship', undefined, true)}
            selectedKey={demographic.relationship || ''}
            options={ClaimDetails.PATIENT_RELATIONSHIP_OPTIONS}
          />
        </div>
      );
    }

    return (
      <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <div>
          <div className="flex">
            <div style={{ flex: 1, marginRight: 10 }}>
              <TextField
                disabled={!this.state.canEdit}
                required={true}
                label="First Name"
                value={demographic.firstName || ''}
                onChange={(a, v) => this.onChangedDemographicField(type, v, 'firstName')}
              />
            </div>
            <div style={{ flexGrow: 0, width: 40, marginRight: 10 }}>
              <TextField
                disabled={!this.state.canEdit}
                label="M.I."
                value={demographic.middleName || ''}
                onChange={(a, v) => this.onChangedDemographicField(type, v, 'middleName')}
              />
            </div>
            <div style={{ flex: 1, marginRight: 10 }}>
              <TextField
                disabled={!this.state.canEdit}
                required={true}
                label="Last Name"
                value={demographic.lastName || ''}
                onChange={(a, v) => this.onChangedDemographicField(type, v, 'lastName')}
              />
            </div>
            <div style={{ flex: 0, flexGrow: 0, marginRight: 10 }}>
              <Dropdown
                disabled={!this.state.canEdit}
                style={{ width: 100 }}
                label="Sex"
                required={true}
                onChange={(a, v) => this.onChangedDemographicField(type, v, 'gender', undefined, true)}
                selectedKey={demographic.gender || ''}
                options={ClaimDetails.PATIENT_SEX_OPTIONS}
              />
            </div>
            {relationshipBlock}
            <div style={{ flex: 1, marginRight: 10 }}>
              <DatePicker
                disabled={!this.state.canEdit}
                label="Date of Birth"
                value={demographic.dob ? new Date(demographic.dob) : new Date()}
                disableAutoFocus={true}
                allowTextInput={true}
                formatDate={this.onFormatDate}
                onSelectDate={(v) => this.onChangedDemographicField(type, v, 'dob')}
              />
            </div>
          </div>
        </div>
        {insuranceBlock}
        {addressBlock}
      </div>
    );
  }

  private onFormatDate = (date?: Date): string => {
    if (!date || isNaN(date.getTime())) {
      return '';
    }
    return date.toISOString().substring(0, 10);
  };

  private renderDetailedStatus(): JSX.Element | null {
    const { claim } = this.state;

    if (claim && claim.payerStatus && claim.payerStatus.categoryCode) {
      return (
        <div>
          <div className="section-header">Payer Status</div>
          <div style={{ fontWeight: 'bold' }}>
            [{claim.payerStatus.categoryCode}]: {ClaimCodeLookup.ClaimStatusCategoriesCodes[claim.payerStatus.categoryCode]}
          </div>
          <div>
            [{claim.payerStatus.statusCode}]: {ClaimCodeLookup.ClaimStatusCodes[claim.payerStatus.statusCode]}
          </div>
        </div>
      );
    }

    return null;
  }

  private renderErrors(): JSX.Element | null {
    const { claim } = this.state;

    if (claim && claim.validationErrors && claim.validationErrors.length > 0) {
      const errorList: JSX.Element[] = claim.validationErrors.map((error: string, index: number) => {
        return <span key={index}>{error}</span>;
      });

      return (
        <div style={{ marginTop: 10 }}>
          <MessageBar messageBarType={MessageBarType.severeWarning} isMultiline={true}>
            <strong>There were issues encountered with this claim.</strong>
            <br />
            {errorList}
          </MessageBar>
        </div>
      );
    }

    return null;
  }

  private renderFinancialsAndDates(): JSX.Element | null {
    const { claim } = this.state;
    if (!claim) {
      return null;
    }

    const eraDate = claim.lastRemittanceAdviceUpdate ? moment(claim.lastRemittanceAdviceUpdate.substring(0, 10)).format('l') : '-';
    const paymentStatusDate = claim.lastPaymentStatusUpdate ? moment(claim.lastPaymentStatusUpdate.substring(0, 10)).format('l') : '-';
    const submittedDate = claim.submittedDate ? moment(claim.submittedDate.substring(0, 10)).format('l') : '-';
    const createdAt = claim.createdAt ? moment(claim.createdAt.substring(0, 10)).format('l') : '-';

    return (
      <div>
        <div className="section-header">Financials</div>
        <table>
          <tbody>
            <tr>
              <td>Payer Paid:</td>
              <td>{claim.payerPaid ? '$' + claim.payerPaid : '-'}</td>
            </tr>
            <tr>
              <td>Patient Responsibility:</td>
              <td>{claim.patientResponsibility ? '$' + claim.patientResponsibility : '-'}</td>
            </tr>
            <tr>
              <td>Patient Paid:</td>
              <td>{claim.amountPatientPaid ? '$' + claim.amountPatientPaid : '-'}</td>
            </tr>
            <tr>
              <td>Total Coverage:</td>
              <td>{claim.totalCoverage ? '$' + claim.totalCoverage : '-'}</td>
            </tr>
            <tr>
              <td>Total Billed:</td>
              <td>{claim.billedAmount ? '$' + claim.billedAmount : '-'}</td>
            </tr>
          </tbody>
        </table>
        <div className="section-header">Dates</div>
        <table>
          <tbody>
            <tr>
              <td>Created at:</td>
              <td>{createdAt}</td>
            </tr>
            <tr>
              <td>Submitted at:</td>
              <td>{submittedDate}</td>
            </tr>
            <tr>
              <td>Latest status update:</td>
              <td>{eraDate}</td>
            </tr>
            <tr>
              <td>Latest payment update:</td>
              <td>{paymentStatusDate}</td>
            </tr>
            <tr>
              <td>ERA received at:</td>
              <td>{eraDate}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  private renderPayer(): JSX.Element | null {
    const { claim } = this.state;
    if (!claim || !claim.payer) {
      return null;
    }

    const payerNameList: IDropdownOption[] = this.state.payerList.map((payer) => {
      return {
        text: payer.name,
        key: payer.id,
      };
    });

    const { address } = claim.payer;

    return (
      <div className="flex">
        <div style={{ flex: 0, flexGrow: 0 }}>
          <Dropdown disabled={!this.state.canEdit} style={{ width: 250 }} label="Name" selectedKey={claim.payer.id} options={payerNameList} onChange={this.onChangedPayer} />
        </div>
        {address && address.city && (
          <div style={{ flex: 1, marginLeft: 10 }}>
            <div style={{ marginBottom: 6 }}>Address</div>
            <div>{address.streetLine_1}</div>
            <div>{address.streetLine_2}</div>
            <div>
              {address.city},{address.state} {address.zip}
            </div>
          </div>
        )}
      </div>
    );
  }

  private onChangedPayer = (e: React.FormEvent, item?: IDropdownOption) => {
    const { claim } = this.state;
    const payer = this.state.payerList.find((p) => !!item && p.id === (item.key as string));
    if (payer && claim) {
      claim.payer = {
        ...claim.payer,
        id: payer.id,
        address: payer.address,
      };
      this.setState({ claim, unsaved: true, shouldComponentUpdate: true });
    }
  };

  private renderBillingProvider(): JSX.Element | null {
    const { claim } = this.state;
    if (!claim || !claim.billingProvider) {
      return null;
    }

    const billingProviderList: IDropdownOption[] = this.state.billingProviderList.map((bp) => {
      return {
        text: `${bp.description}`,
        key: bp.id,
      };
    });

    const { address, taxId } = claim.billingProvider;

    return (
      <div className="flex">
        <div style={{ flex: 0, flexGrow: 0 }}>
          <Dropdown
            disabled={!this.state.canEdit}
            style={{ width: 250 }}
            label="Organization Name"
            selectedKey={claim.billingProvider.id}
            options={billingProviderList}
            onChange={this.onChangedBillingProvider}
          />
          <TextField label="Tax ID" value={taxId} disabled={!this.state.canEdit} onChange={this.onChangeTaxId} />
        </div>
        <div style={{ flex: 1, marginLeft: 10 }}>
          <div style={{ marginBottom: 6 }}>Address</div>
          <div>{address.streetLine_1}</div>
          <div>{address.streetLine_2}</div>
          <div>
            {address.city},{address.state} {address.zip}
          </div>
        </div>
      </div>
    );
  }

  private onChangeTaxId = (e: React.FormEvent, value?: string) => {
    const { claim } = this.state;
    if (claim) {
      claim.billingProvider = {
        ...claim.billingProvider,
        taxId: value,
      };
      this.setState({ claim, unsaved: true, shouldComponentUpdate: true });
    }
  };

  private onChangedBillingProvider = (e: React.FormEvent, item?: IDropdownOption) => {
    const { claim } = this.state;
    const billingProvider = this.state.billingProviderList.find((p) => !!item && p.id === (item.key as number));
    if (billingProvider && claim) {
      claim.billingProvider = {
        ...claim.billingProvider,
        id: billingProvider.id,
        address: billingProvider.address,
      };
      this.setState({ claim, unsaved: true, shouldComponentUpdate: true });
    }
  };

  private renderDiagnosisCodes(): JSX.Element | null {
    const { claim } = this.state;
    if (!claim || !claim.icdCodes) {
      return null;
    }

    const icdRows = claim.icdCodes.map((icdCode: string, i: number) => {
      return (
        <tr key={i}>
          <td>{i + 1}</td>
          <td>
            <TextField disabled={!this.state.canEdit} value={icdCode} onChange={(a, v) => this.onChangedField(v, 'icdCodes', undefined, false, i)} />
          </td>
          <td>{this.state.canEdit && <CommandBarButton style={{ height: 32 }} iconProps={{ iconName: 'Delete' }} onClick={() => this.removeDiagnosisCode(i)} />}</td>
        </tr>
      );
    });

    return (
      <table>
        <tbody>{icdRows}</tbody>
      </table>
    );
  }

  private addDiagnosisCode = () => {
    const { icdCodes } = this.state.claim;
    this.setState({ claim: { ...this.state.claim, icdCodes: [...icdCodes, ''] } });
  };

  private removeDiagnosisCode = (index: number) => {
    const { icdCodes } = this.state.claim;
    this.setState({
      unsaved: true,
      claim: { ...this.state.claim, icdCodes: icdCodes.filter((_: string, i: number) => i !== index) },
    });
  };

  private renderAdjustmentLines(adjustments?: Adjustment[]): JSX.Element[] {
    return (adjustments || []).map((adj, i) => (
      <tr key={i}>
        <td>
          <strong>
            {adj.typeCode}:{adj.reasonCode}
          </strong>{' '}
          - {ClaimCodeLookup.ClaimAdjustmentReasonCodes[adj.reasonCode]}
        </td>
        <td style={{ verticalAlign: 'top' }}>${adj.amount}</td>
      </tr>
    ));
  }

  private renderRemarks(remarks?: Remark[]): JSX.Element[] {
    return (remarks || []).map((rmk, i) => (
      <tr key={i}>
        <td>{ClaimCodeLookup.remarkCode[rmk.value]}</td>
      </tr>
    ));
  }

  private renderServiceLines(): JSX.Element | null {
    const { claim } = this.state;

    if (!claim || !claim.serviceLines) {
      return null;
    }

    let serviceLines: JSX.Element[] = [];

    serviceLines = claim.serviceLines.map((sl: any, index: number) => this.renderServiceLine(sl, index));

    return (
      <table style={{ flex: 1 }}>
        <thead>
          <tr>
            <th>CPT</th>
            <th>Modifiers</th>
            <th>Units</th>
            <th>ICD Pointers</th>
            <th>Bill Amount</th>
            <th>Paid Amount</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>{serviceLines}</tbody>
      </table>
    );
  }

  private addServiceLine = () => {
    const { serviceLines } = this.state.claim;
    const sl = {
      cptCode: '',
      modifiers: '',
      units: '',
      icdPointers: '',
      billedAmount: '',
      paidAmount: '',
    };
    this.setState({ claim: { ...this.state.claim, serviceLines: [...serviceLines, sl] } });
  };

  private removeServiceLine = (index: number) => {
    const { serviceLines } = this.state.claim;
    this.setState({
      unsaved: true,
      claim: { ...this.state.claim, serviceLines: serviceLines.filter((_: any, i: number) => i !== index) },
    });
  };

  private renderServiceLine = (sl: any, index: number) => {
    let adjLine: JSX.Element | null = null;
    const slLine: JSX.Element = (
      <tr key={index}>
        <td>
          <div style={{ width: 80 }}>
            <TextField disabled={!this.state.canEdit} value={sl.cptCode} onChange={(a, v) => this.onChangedField(v, 'serviceLines', 'cptCode', false, index)} />
          </div>
        </td>
        <td>
          <TextField disabled={!this.state.canEdit} value={String(sl.modifiers)} onChange={(a, v) => this.onChangedField(v, 'serviceLines', 'modifiers', false, index)} />
        </td>
        <td>
          <TextField disabled={!this.state.canEdit} value={String(sl.units) || ''} onChange={(a, v) => this.onChangedField(v, 'serviceLines', 'units', false, index)} />
        </td>
        <td>
          <TextField disabled={!this.state.canEdit} value={String(sl.icdPointers)} onChange={(a, v) => this.onChangedField(v, 'serviceLines', 'icdPointers', false, index)} />
        </td>
        <td>
          <TextField
            disabled={!this.state.canEdit}
            value={String(sl.billedAmount || '0.00')}
            onChange={(a, v) => this.onChangedField(v, 'serviceLines', 'billedAmount', false, index)}
          />
        </td>
        <td>{sl.paidAmount ? '$' + sl.paidAmount : '-'}</td>
        <td>{this.state.canEdit && <CommandBarButton style={{ height: 32 }} iconProps={{ iconName: 'Delete' }} onClick={() => this.removeServiceLine(index)} />}</td>
      </tr>
    );

    if (sl.paidAmount || sl.adjustments || sl.status) {
      adjLine = (
        <tr>
          <td colSpan={1}>&nbsp;</td>
          <td colSpan={5}>
            <div style={{ paddingBottom: 20 }}>
              <div className="flex" style={{ padding: 5, backgroundColor: '#DDDDDD' }}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: 'bold' }}>Status:</span>
                  <div style={{ fontWeight: 'bold' }}>{sl.status ? ClaimCodeLookup.ClaimStatusCategoriesCodes[sl.status.categoryCode] : null}</div>
                  <div>{sl.status ? ClaimCodeLookup.ClaimStatusCodes[sl.status.statusCode] : null}</div>
                  <span style={{ fontWeight: 'bold' }}>Allowed Amount:&nbsp;</span>
                  <span>{sl.allowedAmount ? '$' + sl.allowedAmount : ' -'}</span>
                </div>
                <div style={{ flex: 2 }}>
                  <table>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left' }}>Reason</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>{this.renderAdjustmentLines(sl.adjustments)}</tbody>
                  </table>
                  <table>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left' }}>Remarks</th>
                      </tr>
                    </thead>
                    <tbody>{this.renderRemarks(sl.remarkCodes)}</tbody>
                  </table>
                </div>
              </div>
            </div>
          </td>
        </tr>
      );
    }

    return (
      <>
        {slLine}
        {adjLine}
      </>
    );
  };

  private saveClaimDetails = () => {
    let { claim } = this.state;

    if (claim) {
      const serviceLines = this.state.claim.serviceLines.map((sl: any) => ({
        ...sl,
        start: claim.dateOfService,
        end: claim.dateOfService,
        placeOfService: this.state.placeOfService,
        icdPointers: sl.icdPointers.split(','),
        modifiers: sl.modifiers.split(','),
      }));

      claim = {
        ...claim,
        serviceLines,
        dependent: this.state.isDependent ? claim.dependent : null,
      };

      this.setState({ isSaving: true }, () => {
        HealAPI.updateClaimDetails(claim).then((response) => {
          if (response && response.data) {
            if (response.ok) {
              this.setState({ unsaved: false });
              this.props.actions.toast({
                type: 'success',
                title: 'Success',
                content: 'Claim details have been successfully updated.',
              });
            } else {
              this.props.actions.toast({
                type: 'error',
                title: (response.data as any).error || 'Error',
                content: (response.data as any).message || 'An Error occurred, please try again later.',
              });
            }
          } else {
            this.props.actions.toast({
              type: 'error',
              title: 'Error',
              content: 'An Error occurred, please try again later.',
            });
          }
          this.setState({ isSaving: false });
        });
      });
    }
  };
}
